import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/tailwindUtils.js";

/**
 * Table-cell text that reveals its full value in a styled hover card.
 *
 * Why not the native `title` attribute: it is unstyled, waits ~1s before
 * appearing, cannot be themed, and truncates long values itself on some
 * platforms. Why not components/ui/tooltip.js: that one is a compound
 * Trigger/Content API meant for icons and buttons, and it always renders —
 * inside a data table that means a card popping up over every cell whether or
 * not anything is actually cut off.
 *
 * This one:
 *   - measures the node and only arms itself when the text really overflows,
 *     so a short name stays a plain cell with no hover behaviour at all
 *   - renders through a portal on document.body, so the card is never clipped
 *     by the table's `overflow-x-auto` wrapper
 *   - flips above/below and clamps to the viewport near the edges
 *   - supports multi-line cells via `lines` (line-clamp) as well as 1-line truncate
 *
 * Usage:
 *   <TruncatedText text={row.customer_name} className="font-medium" />
 *   <TruncatedText text={row.latest_remarks} lines={2} label="Remarks" />
 */

const GAP = 10;          // distance between cell and card
const EDGE = 8;          // min gap from the viewport edge

// Written out rather than built as `line-clamp-${n}` — Tailwind's JIT only
// emits classes it can see as complete strings in the source.
const CLAMP = {
  1: "truncate",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
};

const TruncatedText = ({
  text,
  className,
  lines = 1,
  label,
  placeholder = "—",
  as: Tag = "span",
}) => {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, side: "top" });

  const value = text == null || text === "" ? null : String(text);

  // ── Does this actually need a tooltip? ────────────────────────────────────
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const cut = lines > 1
      ? el.scrollHeight > el.clientHeight + 1
      : el.scrollWidth > el.clientWidth + 1;
    setOverflowing(cut);
  }, [lines]);

  useLayoutEffect(() => {
    measure();
    if (!window.ResizeObserver || !ref.current) return;
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [measure, value]);

  // ── Where does the card go? ───────────────────────────────────────────────
  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Prefer above; drop below when the cell sits near the top of the screen.
    const side = r.top < 140 ? "bottom" : "top";
    const half = Math.min(360, Math.max(180, r.width)) / 2;
    const left = Math.min(
      window.innerWidth - EDGE - half,
      Math.max(EDGE + half, r.left + r.width / 2)
    );
    setPos({
      top: side === "top" ? r.top - GAP : r.bottom + GAP,
      left,
      side,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  const show = () => { if (overflowing) { place(); setOpen(true); } };
  const hide = () => setOpen(false);

  if (!value) {
    return <span className="text-gray-300 dark:text-gray-600">{placeholder}</span>;
  }

  return (
    <>
      <Tag
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={overflowing ? 0 : undefined}
        className={cn(
          "block outline-none",
          CLAMP[lines] || "truncate",
          // A dotted underline on hover is the only hint that there is more to read.
          overflowing &&
            "cursor-help underline-offset-[3px] hover:underline hover:decoration-dotted focus-visible:underline focus-visible:decoration-dotted",
          className
        )}
      >
        {value}
      </Tag>

      {open && overflowing && createPortal(
        <div
          role="tooltip"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            transform: `translate(-50%, ${pos.side === "top" ? "-100%" : "0"})`,
          }}
          className={cn(
            "pointer-events-none z-[9999] max-w-[360px]",
            "animate-in fade-in-0 zoom-in-95 duration-150",
            pos.side === "top" ? "slide-in-from-bottom-1" : "slide-in-from-top-1"
          )}
        >
          <div
            className={cn(
              "relative rounded-lg px-3 py-2 shadow-xl shadow-slate-900/25",
              "bg-slate-900/95 backdrop-blur-sm ring-1 ring-white/10",
              "dark:bg-slate-800/95 dark:ring-white/15",
              // the accent rail is what makes it read as ours, not the browser's
              "border-l-2 border-indigo-400"
            )}
          >
            {label && (
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
                {label}
              </div>
            )}
            <div className="whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-50">
              {value}
            </div>

            {/* caret */}
            <div
              className={cn(
                "absolute left-1/2 h-2 w-2 -translate-x-1/2 rotate-45",
                "bg-slate-900/95 dark:bg-slate-800/95",
                pos.side === "top"
                  ? "-bottom-1 border-b border-r border-white/10 dark:border-white/15"
                  : "-top-1 border-l border-t border-white/10 dark:border-white/15"
              )}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TruncatedText;
